import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Input, Button, Card, Spin, Alert, Row, Col, Table, Tabs } from "antd";
import { RobotOutlined, SendOutlined, TableOutlined, BarChartOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import Chart from "react-apexcharts";
import AiDataAnalystService, {
    IRestDataAnalystServiceCollectionResponse,
} from "model/service/dataStorage/AiDataAnalystService";
import IRestServiceOptions from "model/interface/api/IRestServiceOptions";
import LocaleText from "../settings/dictionary/LocaleText";
import selectors from "../../../redux/selectors";
import { IAppState } from "../../../redux/store";
import IContentType from "../../../model/interface/dataStorage/IContentType";
import IField from "../../../model/interface/dataStorage/IField";

const { TabPane } = Tabs;

interface IOwnProps {
    contentType: IContentType;
    currentFilters?: Record<string, any>;
    onQueryExecuted?: (results: any[]) => void;
}

interface IReduxProps {
    contentTypes: IContentType[];
    getText: (code: string, fallback?: string) => string;
}

type IProps = IOwnProps & IReduxProps;

const AiDataAnalyst: React.FC<IProps> = ({
                                                          contentType,
                                                          contentTypes,
                                                          currentFilters,
                                                          onQueryExecuted,
                                                          getText,
                                                      }) => {
    const [question, setQuestion] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [response, setResponse] =
        useState<IRestDataAnalystServiceCollectionResponse | null>(null);

    const handleSearch = async () => {
        if (!question.trim()) return;

        setLoading(true);
        try {
            const options: IRestServiceOptions = {
                ...currentFilters,
                q: question,
                entity: contentType.fullClassName,
            };

            const result = await AiDataAnalystService.collectionList(options);
            setResponse(result);

            if (result.success && result.data && onQueryExecuted) {
                onQueryExecuted(result.data);
            }
        } catch (err: any) {
            setResponse({
                success: false,
                dql: "",
                summary: "",
                chart: null,
                data: [],
                error:
                    err.message ||
                    getText(
                        "ai.analyst.error.generic",
                        "Chyba při komunikaci s AI asistentem."
                    ),
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Resolves the user-facing localized title for a given SQL output column.
     * Searches:
     *  1. Current contentType fields (by property name, column name, or normalized format)
     *  2. Master entity fields (if contentType has masterClass)
     *  3. Related entity fields across all setup contentTypes
     *  4. Global dictionary fallback or original alias
     */
    const resolveColumnLabel = (rawKey: string): string => {
        console.log(rawKey);
        if (!rawKey) return "";

        const cleanKey = rawKey.trim();
        const normalized = cleanKey.toLowerCase().replace(/[_\-\s]/g, "");

        const matchField = (fields?: IField[]): IField | undefined => {
            if (!fields || !Array.isArray(fields)) return undefined;
            return fields.find((f: IField) => {
                const fName = (f.name || "").toLowerCase().replace(/[_\-\s]/g, "");
                const colName = ((f as any).columnName || f.name || "").toLowerCase().replace(/[_\-\s]/g, "");
                console.log(fName, colName, normalized);
                return fName === normalized || colName === normalized;
            });
        };

        // 1. Direct match on current contentType
        let matched = matchField(contentType?.fields);

        // 2. Match on masterClass contentType if available
        if (!matched && contentType?.masterClass && contentTypes?.length) {
            const masterCt = contentTypes.find(
                (ct) =>
                    ct.fullClassName === contentType.masterClass ||
                    ct.name === contentType.masterClass
            );
            if (masterCt) {
                matched = matchField(masterCt.fields);
            }
        }

        // 3. Match across any related contentTypes
        if (!matched && contentTypes?.length) {
            for (const ct of contentTypes) {
                matched = matchField(ct.fields);
                if (matched) break;
            }
        }

        if (matched) {
            console.log(matched);
            const labelCode = matched.label || matched.name;
            return getText(labelCode, matched.label || matched.name || cleanKey);
        }

        // 4. Check if the SQL alias matches a translation key directly
        const dictFallback = getText(cleanKey, "");
        if (dictFallback) {
            return dictFallback;
        }

        return cleanKey;
    };

    // Dynamically build Ant Design Table columns using the resolved localized labels
    const tableColumns: ColumnsType<any> = useMemo(() => {
        if (!response?.data || response.data.length === 0) {
            return [];
        }

        const firstRow = response.data[0];
        return Object.keys(firstRow).map((key) => {
            const columnTitle = resolveColumnLabel(key);

            return {
                title: columnTitle,
                dataIndex: key,
                key: key,
                sorter: (a: any, b: any) => {
                    const valA = a[key];
                    const valB = b[key];
                    if (typeof valA === "number" && typeof valB === "number") {
                        return valA - valB;
                    }
                    return String(valA ?? "").localeCompare(String(valB ?? ""));
                },
                render: (value: any) => {
                    if (value === null || value === undefined) {
                        return (
                            <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>
                                null
                            </span>
                        );
                    }
                    if (typeof value === "boolean") {
                        return value
                            ? getText("global.yes", "Ano")
                            : getText("global.no", "Ne");
                    }
                    if (typeof value === "object") {
                        return JSON.stringify(value);
                    }
                    return String(value);
                },
            };
        });
    }, [response?.data, contentType, contentTypes, getText]);

    const hasChart = Boolean(response?.chart);
    const hasData = Boolean(response?.data && response.data.length > 0);

    return (
        <Card
            size="small"
            className="mb-3 border rounded shadow-sm"
            style={{ background: "#fcfcfc" }}
            title={
                <span className="d-flex align-items-center gap-2">
                    <RobotOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                    <strong>
                        <LocaleText
                            code={"ai.analyst.title"}
                            fallback={"AI Datový Analytik"}
                        />
                    </strong>
                </span>
            }
        >
            <Row gutter={[8, 8]}>
                <Col flex="auto">
                    <Input
                        placeholder={getText(
                            "ai.analyst.input.placeholder",
                            "Zadejte dotaz (např. 'Zobrazte počet záznamů v průběhu roku na grafu')..."
                        )}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onPressEnter={handleSearch}
                        disabled={loading}
                        allowClear
                    />
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSearch}
                        loading={loading}
                    >
                        <LocaleText
                            code={"ai.analyst.button.ask"}
                            fallback={"Zeptat se AI"}
                        />
                    </Button>
                </Col>
            </Row>

            {loading && (
                <div className="text-center py-3">
                    <Spin
                        tip={getText(
                            "ai.analyst.loading",
                            "AI zpracovává dotaz a generuje analýzu..."
                        )}
                    />
                </div>
            )}

            {response && !loading && (
                <div className="mt-3">
                    {response.success ? (
                        <>
                            {/* Summary alert */}
                            {response.summary && (
                                <Alert
                                    message={
                                        <LocaleText
                                            code={"ai.analyst.summary.title"}
                                            fallback={"Analýza výsledků"}
                                        />
                                    }
                                    description={
                                        <div style={{ whiteSpace: "pre-wrap" }}>
                                            {response.summary}
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    icon={<RobotOutlined />}
                                    className="mb-3"
                                />
                            )}

                            {/* Chart & Table Display (AntD 4 TabPane) */}
                            {hasChart && hasData ? (
                                <Tabs
                                    defaultActiveKey="chart"
                                    type="card"
                                    className="bg-white p-2 border rounded"
                                >
                                    <TabPane
                                        tab={
                                            <span>
                                                <BarChartOutlined />
                                                <LocaleText
                                                    code="ai.analyst.tab.chart"
                                                    fallback="Graf"
                                                />
                                            </span>
                                        }
                                        key="chart"
                                    >
                                        <Chart
                                            type={response.chart!.type}
                                            series={response.chart!.series}
                                            options={response.chart!.options}
                                            height={320}
                                        />
                                    </TabPane>
                                    <TabPane
                                        tab={
                                            <span>
                                                <TableOutlined />
                                                <LocaleText
                                                    code="ai.analyst.tab.table"
                                                    fallback="Tabulka údajů"
                                                />
                                                {` (${response.data.length})`}
                                            </span>
                                        }
                                        key="table"
                                    >
                                        <Table
                                            size="small"
                                            columns={tableColumns}
                                            dataSource={response.data}
                                            rowKey={(record, index) =>
                                                record.id ||
                                                record.uuid ||
                                                record.virtual_entity_uuid ||
                                                String(index)
                                            }
                                            pagination={{ pageSize: 10, size: "small" }}
                                            scroll={{ x: "max-content" }}
                                            bordered
                                        />
                                    </TabPane>
                                </Tabs>
                            ) : hasChart ? (
                                <div className="bg-white p-3 border rounded mb-3">
                                    <Chart
                                        type={response.chart!.type}
                                        series={response.chart!.series}
                                        options={response.chart!.options}
                                        height={320}
                                    />
                                </div>
                            ) : hasData ? (
                                <div className="bg-white p-2 border rounded mb-3">
                                    <Table
                                        size="small"
                                        columns={tableColumns}
                                        dataSource={response.data}
                                        rowKey={(record, index) =>
                                            record.id ||
                                            record.uuid ||
                                            record.virtual_entity_uuid ||
                                            String(index)
                                        }
                                        pagination={{ pageSize: 10, size: "small" }}
                                        scroll={{ x: "max-content" }}
                                        bordered
                                    />
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <Alert
                            message={
                                <LocaleText
                                    code={"ai.analyst.error.title"}
                                    fallback={"Omlouvám se, na toto nedokážu odpovědět."}
                                />
                            }
                            description={response.error || " "}
                            type="error"
                            showIcon
                        />
                    )}
                </div>
            )}
        </Card>
    );
};

const mapStateToProps = (state: IAppState) => {
    const { contentTypes } = state.setup;
    return {
        contentTypes,
        getText: (code: string, fallback?: string) =>
            selectors.dictionary.getMessage(state, code, fallback),
    };
};

export default connect(mapStateToProps)(AiDataAnalyst);